export type Lang = 'curl' | 'python' | 'node';

/**
 * The three samples on the For developers page. They follow docs/FORMAT.md (version 3) and the Python client
 * (`cli/src/passwork_bridge/crypto.py`): a message key derived from the link key, the text padded to a size class, an
 * envelope `version ‖ suite ‖ flags ‖ sender ‖ seq ‖ iv ‖ ciphertext ‖ tag`, and additional data that binds it to its bridge,
 * its id (16 random bytes, 22 base64url characters, chosen by the sender), its side and its kind. The Node.js sample is run
 * against a real server and its link is opened with the web client in `web/test/developersSamples.test.ts`.
 */
export const SAMPLES: Record<Lang, (base: string) => string> = {
  curl: (base) => `# 1. Create a bridge — the server never sees a key or plaintext
curl -X POST ${base}/api/bridges \\
  -H "Content-Type: application/json" \\
  -d '{"ttlSeconds": 86400, "maxViews": 1}'

# → {"bridgeId":"K7mN9pQr…","ownerToken":"…","createdAt":"…","expiresAt":"…","maxViews":1,"mode":"secret"}

# 2. You choose the message id: 16 random bytes, 22 base64url characters
MESSAGE_ID=$(openssl rand -base64 16 | tr '+/' '-_' | tr -d '=\\n')

# 3. Encrypt on your side. The link key (32 random bytes) never encrypts: derive the message key from it with
#    HKDF-SHA-256, pad the text, seal it with AES-256-GCM and put the result in an envelope. The bytes of every step
#    are in docs/FORMAT.md; the Python and Node.js samples do it in a few lines. Send base64url(envelope).
curl -X POST ${base}/api/bridges/$BRIDGE_ID/messages \\
  -H "Authorization: Bearer $OWNER_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"id":"'$MESSAGE_ID'","kind":"text","body":"'$ENVELOPE'"}'

# 4. Share ${base}/b/$BRIDGE_ID#$LINK_KEY — the key stays in the fragment`,
  python: (base) => `# pip install cryptography requests
import base64, os, struct, requests
from cryptography.hazmat.primitives import hashes
from cryptography.hazmat.primitives.ciphers.aead import AESGCM
from cryptography.hazmat.primitives.kdf.hkdf import HKDF

def b64url(data: bytes) -> str:
    return base64.urlsafe_b64encode(data).rstrip(b"=").decode()

def lp(data: bytes) -> bytes:  # a field with its length in front
    return struct.pack(">H", len(data)) + data

bridge = requests.post("${base}/api/bridges",
    json={"ttlSeconds": 86400, "maxViews": 1}).json()

# the link key goes in the link and never encrypts: the message key is derived from it
link_key = os.urandom(32)
key = HKDF(hashes.SHA256(), 32, None, b"passwork-bridge/v3/key/message").derive(link_key)

# pad to a size class: u32(length) ‖ text ‖ zeros (longer texts: Padmé, docs/FORMAT.md §3)
text = b"hunter2"
size = next(n for n in (256, 1024, 4096) if n >= len(text) + 4)
padded = struct.pack(">I", len(text)) + text
padded += bytes(size - len(padded))

# you choose the message id (16 random bytes, 22 characters) and your device's name on this bridge (8 random bytes);
# the text is bound to its bridge, its id, who wrote it (1 = owner), what it is (1 = text) and its place in your run
message_id = b64url(os.urandom(16))
sender, seq, flags = os.urandom(8), struct.pack(">I", 0), 0x02
aad = (b"passwork-bridge/message" + bytes([0, 3, 1, flags]) + lp(bridge["bridgeId"].encode())
       + lp(message_id.encode()) + bytes([1, 1]) + sender + seq)
iv = os.urandom(12)
body = b64url(bytes([3, 1, flags]) + sender + seq + iv + AESGCM(key).encrypt(iv, padded, aad))

requests.post(f"${base}/api/bridges/{bridge['bridgeId']}/messages",
    headers={"Authorization": f"Bearer {bridge['ownerToken']}"},
    json={"id": message_id, "kind": "text", "body": body}).raise_for_status()

print(f"${base}/b/{bridge['bridgeId']}#{b64url(link_key)}")`,
  node: (base) => `// Node 22 — Web Crypto is built in
const b64url = (bytes) => Buffer.from(bytes).toString('base64url');
const cat = (...parts) => Buffer.concat(parts.map((part) => Buffer.from(part)));
const utf8 = (text) => new TextEncoder().encode(text);
const u32 = (n) => Uint8Array.of(n >>> 24, n >>> 16, n >>> 8, n);
const lp = (bytes) => cat(Uint8Array.of(bytes.length >> 8, bytes.length), bytes); // a field with its length in front

const bridge = await fetch('${base}/api/bridges', {
  method: 'POST', headers: { 'content-type': 'application/json' },
  body: JSON.stringify({ ttlSeconds: 86400, maxViews: 1 }),
}).then((r) => r.json());

// the link key goes in the link and never encrypts: the message key is derived from it
const linkKey = crypto.getRandomValues(new Uint8Array(32));
const hkdfKey = await crypto.subtle.importKey('raw', linkKey, 'HKDF', false, ['deriveKey']);
const key = await crypto.subtle.deriveKey(
  { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: utf8('passwork-bridge/v3/key/message') },
  hkdfKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);

// pad to a size class: u32(length) ‖ text ‖ zeros (longer texts: Padmé, docs/FORMAT.md §3)
const text = utf8('hunter2');
const size = [256, 1024, 4096].find((n) => n >= text.length + 4);
if (size === undefined) throw new Error('A longer text needs the Padmé padding of docs/FORMAT.md');
const padded = new Uint8Array(size);
padded.set(u32(text.length));
padded.set(text, 4);

// you choose the message id (16 random bytes, 22 characters) and your device's name on this bridge (8 random bytes);
// the text is bound to its bridge, its id, who wrote it (1 = owner), what it is (1 = text) and its place in your run
const id = b64url(crypto.getRandomValues(new Uint8Array(16)));
const sender = crypto.getRandomValues(new Uint8Array(8));
const seq = u32(0);
const flags = 0x02;
const additionalData = cat(utf8('passwork-bridge/message'), [0, 3, 1, flags], lp(utf8(bridge.bridgeId)), lp(utf8(id)), [1, 1], sender, seq);
const iv = crypto.getRandomValues(new Uint8Array(12));
const sealed = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData }, key, padded);
const body = b64url(cat([3, 1, flags], sender, seq, iv, new Uint8Array(sealed)));

const sent = await fetch('${base}/api/bridges/' + bridge.bridgeId + '/messages', {
  method: 'POST',
  headers: { 'content-type': 'application/json', authorization: 'Bearer ' + bridge.ownerToken },
  body: JSON.stringify({ id, kind: 'text', body }),
});
if (!sent.ok) throw new Error('The server answered ' + sent.status);

console.log('${base}/b/' + bridge.bridgeId + '#' + b64url(linkKey));`,
};
